import { useSyncExternalStore } from "react";
import { newStudyId } from "./studyProtocol.mjs";

const PARTICIPANT_KEY = "tears-gers-participant";
const SESSION_KEY = "tears-gers-session";
const OWNER_KEY = "tears-gers-session-participant";
const CHANGE_EVENT = "tears-gers-session-changed";
let snapshot;

function read(storage, key) {
  try { return window[storage].getItem(key); } catch { return undefined; }
}
function write(storage, key, value) {
  try { window[storage].setItem(key, value); } catch { /* Keep the active in-memory identity. */ }
}

export function currentStudyIdentity() {
  const storedParticipant = read("localStorage", PARTICIPANT_KEY);
  const participantId = storedParticipant === undefined
    ? snapshot?.participantId || newStudyId("participant")
    : storedParticipant || newStudyId("participant");
  const owner = read("sessionStorage", OWNER_KEY);
  let sessionId = read("sessionStorage", SESSION_KEY);
  // Another tab may change the participant. Never reuse the previous person's
  // session in this tab, even if the old sessionStorage entry still exists.
  if ((owner && owner !== participantId) || (snapshot && snapshot.participantId !== participantId)) {
    sessionId = newStudyId("session");
  }
  sessionId = sessionId === undefined
    ? snapshot?.sessionId || newStudyId("session")
    : sessionId || newStudyId("session");
  write("localStorage", PARTICIPANT_KEY, participantId);
  write("sessionStorage", SESSION_KEY, sessionId);
  write("sessionStorage", OWNER_KEY, participantId);
  if (snapshot?.participantId !== participantId || snapshot?.sessionId !== sessionId) {
    snapshot = { participantId, sessionId };
  }
  return snapshot;
}

function subscribe(callback) {
  const onStorage = event => {
    if (event.key === PARTICIPANT_KEY || event.key === SESSION_KEY || event.key === null) callback();
  };
  window.addEventListener("storage", onStorage);
  window.addEventListener(CHANGE_EVENT, callback);
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(CHANGE_EVENT, callback);
  };
}

export function useStudyIdentity() {
  return useSyncExternalStore(subscribe, currentStudyIdentity);
}

export function resetStudySession({ newParticipant = false } = {}) {
  const previous = currentStudyIdentity();
  // Clear only this active session's browser working state. The backend study
  // log and unrelated participants/sessions are never modified.
  const prefixes = [
    `recommendation-session-v4:${previous.participantId}:${previous.sessionId}:`,
    `catalog-session-v4:${previous.participantId}:${previous.sessionId}`,
    `tears-movie-ratings:${previous.participantId}:${previous.sessionId}`,
    `gers-movie-ratings:${previous.participantId}:${previous.sessionId}`,
  ];
  try {
    Object.keys(sessionStorage).filter(key => prefixes.some((prefix, index) =>
      index === 0 ? key.startsWith(prefix) : key === prefix
    )).forEach(key => sessionStorage.removeItem(key));
  } catch { /* A new namespace also resets in-memory state without storage. */ }
  snapshot = {
    participantId: newParticipant ? newStudyId("participant") : previous.participantId,
    sessionId: newStudyId("session"),
  };
  write("localStorage", PARTICIPANT_KEY, snapshot.participantId);
  write("sessionStorage", SESSION_KEY, snapshot.sessionId);
  write("sessionStorage", OWNER_KEY, snapshot.participantId);
  window.dispatchEvent(new Event(CHANGE_EVENT));
  return snapshot;
}
